import { useEffect, useState } from "react";
import { Moon, Sun } from "lucide-react";

/**
 * Toggle dark / light mode pour CATHO+ paroisses.
 *
 * - Persiste le choix utilisateur dans `localStorage` sous la clef `catho-theme`.
 * - Au premier rendu, retombe sur `prefers-color-scheme` si aucune valeur n'est
 *   stockée (respecte le réglage OS).
 * - Pose / retire la classe `.dark` sur `<html>` — Tailwind est configuré en
 *   `darkMode: ["class"]` (cf. `tailwind.config.ts`) et le bloc `.dark` dans
 *   `client/src/index.css` bascule les 140 variants `--brand-*` automatiquement.
 *
 * Le composant est volontairement self-contained (pas de Context) : un toggle
 * unique monté dans `AppShell` suffit pour piloter l'ensemble de l'app.
 */
const STORAGE_KEY = "catho-theme";

export interface DarkModeToggleProps {
  /**
   * Contexte visuel :
   *  - `surface` (défaut) : sur fond clair / carte (barre d'outils flottante)
   *  - `onDark` : intégré au bandeau dégradé bleu marine du DA (icônes blanches)
   */
  variant?: "surface" | "onDark";
}

export function DarkModeToggle({ variant = "surface" }: DarkModeToggleProps) {
  const [isDark, setIsDark] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === "dark") return true;
    if (stored === "light") return false;
    return window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false;
  });

  useEffect(() => {
    const root = document.documentElement;
    if (isDark) {
      root.classList.add("dark");
      window.localStorage.setItem(STORAGE_KEY, "dark");
    } else {
      root.classList.remove("dark");
      window.localStorage.setItem(STORAGE_KEY, "light");
    }
  }, [isDark]);

  const onDark = variant === "onDark";

  return (
    <button
      type="button"
      onClick={() => setIsDark((v) => !v)}
      aria-label={isDark ? "Activer mode clair" : "Activer mode sombre"}
      aria-pressed={isDark}
      data-testid="button-dark-mode-toggle"
      className={
        onDark
          ? "p-1.5 rounded-lg hover:bg-white/10 transition-colors text-white/60 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/40"
          : "p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-gray-600 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-brand-accent/40"
      }
    >
      {isDark ? (
        <Sun className={onDark ? "w-4 h-4" : "w-4 h-4 text-yellow-500"} aria-hidden="true" />
      ) : (
        <Moon className={onDark ? "w-4 h-4" : "w-4 h-4 text-gray-600 dark:text-gray-300"} aria-hidden="true" />
      )}
    </button>
  );
}

export default DarkModeToggle;
